import React, { ReactElement, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import { initialLoginFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { EButtonVariants } from 'constants/Buttons';
import { useAuth } from 'models/authContext';
import { ILoginForm, LoginType } from 'models/auth';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { Alert } from 'components/atoms/Alert';
import { AuthTemplate } from 'components/templates/AuthTemplate';
import { useNavigate } from 'react-router-dom';
import { StyledPageSubTitle, StyledPageTitle } from 'theme/styles';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { AccountType } from 'constants/AccountType';
import { useTranslation } from 'react-i18next';
import TwoFactorChoosePage from 'pages/TwoFactorChoosePage';
import { schema } from './schemaValidation';

export interface ILoginPageProps {
  type: AccountType | null;
  email: string;
  changePage: (p: number) => void;
  setData: (IData: any) => void;
}

export const FC_LICENSING_DEVICE_ID = 'FC_LICENSING_DEVICE_ID';

const LoginPage = ({ type, email, changePage, setData }: ILoginPageProps): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState<ILoginForm | null>(null);
  const [showChooser, setShowChooser] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: initialLoginFormValues, resolver: yupResolver(schema) });
  const { logIn, authMessage, isLoginLoading, clearAuthErrors } = useAuth();
  const { samePageAlert, authAlert, clearAlert } = useAlert();

  useEffect(
    () => () => {
      clearAuthErrors();
    },
    [],
  );
  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_AUTH_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (email) {
      setValue('email', email);
    }
  }, []);

  const onSubmit = (data: ILoginForm) => {
    setLoginData(data);
    setShowChooser(true);
  };

  const onLoginHandler = (twoFactorMethod: LoginType) => {
    if (loginData) {
      logIn(
        {
          password: loginData.password,
          username: loginData.email,
          type,
          loginType: twoFactorMethod,
        },
        (res: any) => {
          if (res) {
            setData({
              password: loginData.password,
              email: loginData.email,
              type,
              loginType: twoFactorMethod,
            });
            changePage(3);
          } else {
            setShowChooser(false);
            changePage(2);
          }
        },
      );
    }
  };

  return (
    <AuthTemplate>
      {showChooser ? (
        <TwoFactorChoosePage onSubmit={onLoginHandler} changePage={changePage} />
      ) : (
        <Grid item container xs={12} gap={7}>
          <Grid item container xs={12} gap={1}>
            <Grid item xs={12}>
              <StyledPageTitle variant="h4">
                {t(`page.signin.loginAs.${type}`)}
              </StyledPageTitle>
            </Grid>
            <Grid item xs={12}>
              <StyledPageSubTitle variant="body2">
                {t(`page.signin.loginAs.${type}.description`)}
              </StyledPageSubTitle>
            </Grid>
          </Grid>

          <form onSubmit={handleSubmit(onSubmit as any)} style={{ width: '100%' }}>
            {samePageAlert && !authAlert && !authMessage.text && (
              <Grid item xs={12} mb={2}>
                <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
              </Grid>
            )}
            <Grid item container xs={12} gap={3}>
              <Grid item xs={12} gap={2}>
                <Grid item xs={12}>
                  <FormInput
                    name="email"
                    label={t('page.signin.input.email')}
                    placeholder={t('page.signin.input.email')}
                    control={control}
                    errors={errors}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: { xs: 32, sm: 40 },
                      },
                    }}
                  />
                  <FormInput
                    name="password"
                    type={EFormInputTypes.password}
                    label={t('page.signin.input.password')}
                    placeholder={t('page.signin.input.password')}
                    control={control}
                    errors={errors}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: { xs: 32, sm: 40 },
                      },
                      '& .MuiOutlinedInput-input': {
                        height: { xs: 32, sm: 40 },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {authAlert && (
                <Grid item xs={12} mb={2}>
                  <Alert text={authAlert.text} variant={authAlert.variant} />
                </Grid>
              )}
              {authMessage.text && !authAlert && (
                <Grid item xs={12} mb={2}>
                  <Alert text={authMessage.text} variant={authMessage.variant} />
                </Grid>
              )}

              <Grid item container xs={12} gap={1.3} justifyItems="center">
                <Grid item xs={12}>
                  <Button
                    id="login"
                    type="submit"
                    label={t('page.signin.button.login')}
                    variant={EButtonVariants.contained}
                    isLoading={isLoginLoading}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    id="signin"
                    label={t('page.signin.button.createAccount')}
                    variant={EButtonVariants.outlined}
                    isLoading={isLoginLoading}
                    fullWidth
                    onClick={() => changePage(4)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    id="forgotPassword"
                    label={t('page.signin.button.forgotPassword')}
                    variant={EButtonVariants.text}
                    isLoading={isLoginLoading}
                    fullWidth
                    onClick={() => navigate(`${ERouteLinks.ForgotPassword}?type=${type}`)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    id="forgotPassword"
                    label={t('page.signin.button.backToStart')}
                    variant={EButtonVariants.text}
                    isLoading={isLoginLoading}
                    fullWidth
                    onClick={() => changePage(1)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Grid>
      )}
    </AuthTemplate>
  );
};
export { LoginPage };
